import type { BlsMode, BlsState } from "./blsChannel";

/** A line, not a paragraph — and the API refuses more. */
export const MAX_SET_NOTE_LENGTH = 280;
export const SUD_VALUES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;
export const VOC_VALUES = [1, 2, 3, 4, 5, 6, 7] as const;

/** One set as the API returns it. */
export type EmdrSetRecord = {
    id: string;
    session_id: string;
    set_number: number;
    passes: number;
    cycles_per_minute: number;
    mode: BlsMode;
    tactile: boolean;
    sud: number | null;
    voc: number | null;
    note: string | null;
    recorded_at: string;
};

export type EmdrSetDraft = {
    passes: number;
    cycles_per_minute: number;
    mode: BlsMode;
    tactile: boolean;
    sud: number | null;
    voc: number | null;
    note: string | null;
};

export type EmdrSetErrorCode = "individualOnly" | "notAdopted" | "generic";

/**
 * Saving a set's ratings, from the therapist's side only.
 *
 * Ratings go over REST, never over the call's data channel: that channel
 * reaches every participant, and the patient's browser has no business
 * holding the record.
 */
export type EmdrSetRecorder = {
    sets: EmdrSetRecord[];
    /** The first load of the sets is still on its way. */
    loading?: boolean;
    saving: boolean;
    error: EmdrSetErrorCode | null;
    record: (draft: EmdrSetDraft) => Promise<void>;
    remove: (id: string) => Promise<void>;
    /**
     * The form says it holds ratings not saved yet, and how to save them —
     * or null once it holds none. Kept so whatever ends the session can.
     */
    holdPending?: (flush: (() => Promise<void>) | null) => void;
    /** Save whatever the form still holds. Resolves when it is stored. */
    flushPending?: () => Promise<void>;
};

/**
 * Whether a session can carry EMDR ratings: one patient's, never a couple's
 * or a group's (see the set records spec — a shared record is not erased when
 * one of its people asks to be).
 */
export function isIndividualSession(session: {
    patient_id?: string | null;
    couple_patient_id?: string | null;
    group_patient_id?: string | null;
}): boolean {
    return Boolean(session.patient_id) && !session.couple_patient_id && !session.group_patient_id;
}

/** The stimulation part of a set, read off the paused state; the ratings come from the therapist. */
export function draftFromState(
    state: BlsState,
    ratings: { sud: number | null; voc: number | null; note: string }
): EmdrSetDraft {
    const note = ratings.note.trim();
    return {
        passes: Math.floor(state.cycles),
        cycles_per_minute: state.settings.cyclesPerMinute,
        mode: state.settings.mode,
        tactile: state.settings.tactile,
        sud: ratings.sud,
        voc: ratings.voc,
        note: note ? note.slice(0, MAX_SET_NOTE_LENGTH) : null,
    };
}

/** What a failed save means, read from the API's error detail. */
export function errorCodeFrom(detail: unknown): EmdrSetErrorCode {
    if (detail === "emdr_sets_individual_only") return "individualOnly";
    if (detail === "emdr_tool_not_adopted") return "notAdopted";
    return "generic";
}
